// ── School supplier identifiers — data model ─────────────────────────────────
// A school has zero or many "customer numbers" / "codes" at each supplier
// (ChefsCulinar Kd.PZ etc.).
// Each identifier is time-bounded (SCD Type 2 — never overwrite, only supersede).
// Suppliers that never change (PCA Schulcode, DATEV Personalkostenstelle) live
// as flat fields on the School record instead — see schulenApi.ts.
//
// Lieferant metadata lives in the Lieferanten module:
//   src/modules/verwaltung/lieferanten/lib/types.ts
// This file re-exports the Lieferant type for convenience.

// Re-export Lieferant from the Lieferanten module (built by Agent A in parallel).
export type { Lieferant } from '../../verwaltung/lieferanten/lib/types'

// ---------------------------------------------------------------------------
// @deprecated Use lieferantId (string FK into Lieferanten module) instead.
// Kept for backwards-compatibility during migration; will be removed in Wave 2.
// ---------------------------------------------------------------------------
/** @deprecated Use lieferantId string FK instead. */
export type SupplierKind =
  | 'chefsculinar'
  | 'pca-mammas'
  | 'pca-schulcatering'
  | 'datev'
  | 'mensamax'
  | 'sanella'
  | 'other'

/** @deprecated Use Lieferant from the Lieferanten module instead. */
export interface SupplierKindInfo {
  kind: SupplierKind
  label: string
  shortLabel: string
  identifierLabel: string
  icon: string
}

/** @deprecated Use useLieferanten() from lieferantenApi instead. */
export const SUPPLIER_KINDS: Record<SupplierKind, SupplierKindInfo> = {
  'chefsculinar':       { kind: 'chefsculinar',       label: 'ChefsCulinar',                shortLabel: 'ChefsCulinar', identifierLabel: 'Kd. PZ',                icon: 'restaurant' },
  'pca-mammas':         { kind: 'pca-mammas',         label: 'PCA — Mammas Canteen GmbH',   shortLabel: 'PCA MC',       identifierLabel: 'Schulcode',             icon: 'point_of_sale' },
  'pca-schulcatering':  { kind: 'pca-schulcatering',  label: 'PCA — Schulcatering GmbH',    shortLabel: 'PCA SC',       identifierLabel: 'Schulcode',             icon: 'point_of_sale' },
  'datev':              { kind: 'datev',              label: 'DATEV (LODAS)',               shortLabel: 'DATEV',        identifierLabel: 'Personalkostenstelle',  icon: 'badge' },
  'mensamax':           { kind: 'mensamax',           label: 'MensaMax (Legacy)',           shortLabel: 'MensaMax',     identifierLabel: 'Schulkennung',          icon: 'history' },
  'sanella':            { kind: 'sanella',            label: 'Sanella',                     shortLabel: 'Sanella',      identifierLabel: 'Kundennummer',          icon: 'local_shipping' },
  'other':              { kind: 'other',              label: 'Sonstiger Lieferant',         shortLabel: 'Sonstige',     identifierLabel: 'Kennung',               icon: 'inventory' },
}
// ---------------------------------------------------------------------------

export interface SchoolSupplierIdentifier {
  id: string
  schoolId: string
  /** FK into Lieferanten module — e.g. 'chefsculinar', 'mensamax'. */
  lieferantId: string
  customerNumber: string        // The actual ID value (e.g., "100247")
  validFrom: string             // ISO date
  validTo: string | null        // ISO date — null = currently valid
  notes: string | null
  changedBy: string             // username
  changedAt: string             // ISO datetime
  source: 'manual' | 'import' | 'migration'  // how this row was created
}

// Helper: is an identifier currently valid?
export function isCurrentlyValid(id: SchoolSupplierIdentifier, asOf: string = new Date().toISOString().slice(0, 10)): boolean {
  if (id.validFrom > asOf) return false
  if (id.validTo && id.validTo < asOf) return false
  return true
}

// Helper: get current identifier for a school + lieferant combination
export function getCurrentIdentifier(
  identifiers: SchoolSupplierIdentifier[],
  schoolId: string,
  lieferantId: string,
  asOf: string = new Date().toISOString().slice(0, 10),
): SchoolSupplierIdentifier | null {
  const matches = identifiers
    .filter((i) => i.schoolId === schoolId && i.lieferantId === lieferantId && isCurrentlyValid(i, asOf))
    .sort((a, b) => b.validFrom.localeCompare(a.validFrom))
  return matches[0] ?? null
}
